import React from "react";
import SparklineChart from "./SparklineChart";
import { getDeltaDisplay } from "./deltaDisplay";
import { InfoBadge } from "./MetricsTable";

interface HeroMetricCardProps {
  label: string;
  subtitle?: string;
  value: number | null; // null → "—"
  prevValue: number | null;
  formatFn: (v: number) => string;
  /** 12-month trend series (oldest → newest). Omit to hide the sparkline. */
  trend?: (number | null)[];
  /**
   * Concrete previous-period range (e.g. "01.04 – 30.04"), shown in the
   * comparison label so "vs poprzedni okres" is unambiguous.
   */
  comparisonPeriodLabel?: string;
  /**
   * "Lower is better" metric (churn / CAC / payback): an INCREASE is red,
   * a decrease green. Arrows stay directional. Default false.
   */
  invertDelta?: boolean;
  /**
   * When set, an info-badge (ⓘ) is rendered next to the label with this text
   * as tooltip. Mirrors `MetricRow.infoBadgeTitle` in {@link MetricsTable}.
   */
  infoBadgeTitle?: string;
}

/**
 * A headline KPI tile for the Hero-strip: large value, period-over-period delta
 * (coloured + directional arrow + text), and a 12-month sparkline. Always
 * rendered; a null value falls back to "—". Reuses {@link getDeltaDisplay}
 * (which wraps `calcDeltaPct`) so the delta logic matches the table and cards.
 */
const HeroMetricCard: React.FC<HeroMetricCardProps> = ({
  label,
  subtitle,
  value,
  prevValue,
  formatFn,
  trend,
  comparisonPeriodLabel,
  invertDelta = false,
  infoBadgeTitle,
}) => {
  const displayValue = value === null ? "—" : formatFn(value);
  const delta = getDeltaDisplay(value, prevValue, invertDelta);

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase text-neutral-500">
            {label}
            {infoBadgeTitle && <InfoBadge title={infoBadgeTitle} />}
          </div>
          {subtitle && (
            <div className="mt-0.5 text-xs text-neutral-400">{subtitle}</div>
          )}
        </div>
        {trend && (
          // Sparkline is decorative-adjacent here; hidden on the narrowest
          // screens to keep the hero value legible.
          <span className="hidden shrink-0 sm:inline-flex">
            <SparklineChart
              values={trend}
              formatFn={formatFn}
              invertDelta={invertDelta}
              metricLabel={label}
            />
          </span>
        )}
      </div>

      <div className="mt-2 text-2xl font-semibold text-neutral-900">
        {displayValue}
      </div>

      <div className="mt-2 flex items-center gap-2 text-xs">
        <span
          className={`font-medium ${delta.colorClass}`}
          aria-label={
            delta.pct === null ? "brak danych do porównania" : undefined
          }
        >
          {delta.arrow && (
            <span aria-hidden="true" className="mr-0.5">
              {delta.arrow}
            </span>
          )}
          {delta.text}
        </span>
        <span className="truncate text-neutral-400">
          vs {prevValue === null ? "—" : formatFn(prevValue)}
          {comparisonPeriodLabel ? ` (${comparisonPeriodLabel})` : ""}
        </span>
      </div>
    </div>
  );
};

export default HeroMetricCard;
